import { isOverdue, type ServerTask } from '../../data/tasks';
import type { Store } from '../../data/types';

export type GroupBy = '' | 'district' | 'store';

export interface TaskGroupRows {
  key: string;
  label: string;
  tasks: ServerTask[];
  overdue: number;
}

/**
 * The Tasks list's groups (owner ask 2026-10-29): by district (trimmed,
 * case-insensitive; a blank or unknown district → "No district") or by store
 * ("00012 — Main St (District)"; a store not in the map → "Unknown store").
 * Districts A–Z and stores by number, the catch-all group last; tasks keep
 * the list's own order inside a group. `overdue` = the app's overdue rule
 * (`isOverdue`: open or reopened, past its Eastern due day).
 */
export function groupTasks(tasks: readonly ServerTask[], by: Exclude<GroupBy, ''>, storeById: ReadonlyMap<number, Store>, now = new Date()): TaskGroupRows[] {
  const out = new Map<string, TaskGroupRows & { order: string; last: boolean }>();
  for (const t of tasks) {
    const store = storeById.get(t.storeId);
    const district = store?.district.trim() ?? '';
    const last = by === 'district' ? district === '' : store === undefined;
    const key = by === 'district' ? district.toLowerCase() : String(t.storeId);
    const label =
      by === 'district'
        ? district || 'No district'
        : store
          ? `${store.storeNumber} — ${store.name}${district ? ` (${district})` : ''}`
          : 'Unknown store';
    const order = by === 'district' ? district.toLowerCase() : (store?.storeNumber ?? '');
    const g = out.get(key) ?? { key, label, tasks: [], overdue: 0, order, last };
    g.tasks.push(t);
    if (isOverdue(t, now)) g.overdue += 1;
    out.set(key, g);
  }
  return [...out.values()]
    .sort((a, b) => Number(a.last) - Number(b.last) || a.order.localeCompare(b.order, undefined, { numeric: true }))
    .map(({ key, label, tasks: rows, overdue }) => ({ key, label, tasks: rows, overdue }));
}
